import { useQuery } from "@tanstack/react-query";
import { apiGet } from "./client";
import { useTicketSize, type TicketSize } from "../ticket-size";

export interface SparkPoint {
  index: number;
  value: number;
}

/** "Other" is a real value on `bankdwn_risk_rating_final` (~5% of rows, ungraded/unrated deals). */
export type RiskGrade = "A" | "B1" | "B2" | "B3" | "Other";

export interface ActualVsTargetPoint {
  grade: RiskGrade;
  bucket: string;
  actualYield: number;
  targetYield: number;
  volume: number;
  office: string;
  borrowerIndustry: string;
}

export interface StateOfPlaySummary {
  blendedRav: { current: number; previousPeriod: number; sparkline: SparkPoint[] };
  segmentsBelowFloor: { count: number; sparkline: SparkPoint[] };
  /** The Annualized Loss page's headline figure for the matured book; null when there is no data. */
  annualizedLoss: { current: number | null; previousPeriod: number | null; sparkline: SparkPoint[] };
  actualVsTarget: ActualVsTargetPoint[];
  yieldTrend: Array<{
    month: string;
    grossOriginations: number;
    grossPortfolio: number;
    netOriginations: number;
    netPortfolio: number;
    requiredYield: number;
    actualYield: number;
  }>;
  elTrend: Array<{ month: string; projectedEl: number; realizedLoss: number }>;
}

export const DEAL_SIZE_BUCKETS = ["<25K", "25-50K", "50-250K", "250K-1M", "1M+"] as const;
export const RISK_GRADES: RiskGrade[] = ["A", "B1", "B2", "B3"];

// Office options are fetched live via useOffices(); industries are fetched
// live via useIndustries(office), scoped to that office's real calibration
// data -- see ./reference. No fixed lists.

export const GRADE_COLORS: Record<RiskGrade, string> = {
  A: "var(--grade-a)",
  B1: "var(--grade-b1)",
  B2: "var(--grade-b2)",
  B3: "var(--grade-b3)",
  Other: "var(--muted-foreground)",
};

export interface StateOfPlayFilters {
  /** office_number */
  office?: number;
  borrowerIndustry: string;
}

export const stateOfPlayKeys = {
  summary: (filters: StateOfPlayFilters, ticket?: TicketSize) =>
    ["state-of-play", "summary", filters.office ?? "all", filters.borrowerIndustry, ticket ?? "all"] as const,
};

export function fetchStateOfPlaySummary(filters: StateOfPlayFilters, ticket?: TicketSize) {
  return apiGet<StateOfPlaySummary>("/state-of-play/summary", {
    office: filters.office != null ? String(filters.office) : undefined,
    borrowerIndustry: filters.borrowerIndustry,
    ticket,
  });
}

export function useStateOfPlaySummary(filters: StateOfPlayFilters) {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: stateOfPlayKeys.summary(filters, ticket),
    queryFn: () => fetchStateOfPlaySummary(filters, ticket),
    staleTime: 60_000,
  });
}
